import React, { useCallback, useMemo } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import { useQueryClient } from '@tanstack/react-query';
import {
  Grid, Heading, Link, Alert,
} from '@printdeal/ui-library';
import { useLocation } from '@gatsbyjs/reach-router';
import queryString from 'query-string';

// Contexts
import OrderDetailContext from '../contexts/OrderDetailContext';

// Hooks
import { useSubmittedDesignCookie } from '../../../../hooks/useSubmittedDesignCookie';

// Helpers
import { dispatchAccountEvent } from '../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

// Components
import OrderDetailBreadCrumbs from './OrderDetailBreadCrumbs';
import LineItemList from './LineItemList/LineItemList';
import OrderSummary from './OrderSummary/OrderSummary';
import PayOrderButton from '../shared/Actions/PayOrder/PayOrderButton';

// Types
import { SanitizedOrder } from '../../../../types/order/Order';
// @ts-expect-error
import { messages } from '../../translations/orders';

interface OrderDetailPageProps {
  order: SanitizedOrder;
}

const OrderDetailWrapper = ({
  order,
}: OrderDetailPageProps) => {
  useSubmittedDesignCookie();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { authResult } = queryString.parse(location.search);

  // ReFetch the order whenever needed
  const reFetchOrder = useCallback(async ({
    reFetchPaymentMethods = false,
    reFetchMandate = false,
    reFetchPaymentLink = false,
  }) => {
    const queryCache = queryClient.getQueryCache();
    const queryList = queryCache.getAll();

    // ReFetch the order data itself
    await queryClient.refetchQueries({
      queryKey: ['sanitized-order', order.id],
    });

    if (reFetchPaymentMethods) {
      // ReFetch the valid paymentMethods
      await queryClient.refetchQueries({
        queryKey: ['order', 'get-payment-methods', order.id],
      });
    }

    if (reFetchMandate) {
      // ReFetch the mandate
      const mandateQuery = queryList.find((query) => {
        const queryKey = query.queryKey as Array<string | number>;
        return queryKey[0] === 'order' && queryKey[1] === 'get-mandate' && queryKey[2] === order.id;
      });

      if (mandateQuery) {
        await queryClient.refetchQueries({
          queryKey: ['order', 'get-mandate', order.id],
        });
      }
    }

    if (reFetchPaymentLink) {
      // ReFetch the paymentLink
      await queryClient.refetchQueries({
        queryKey: ['order', 'get-payment-link', order.id],
      });
    }
  }, [order.id, order.version]);

  const OrderDetailContextContent = useMemo(() => ({
    order,
    reFetchOrder,
  }), [order.id, order.version, reFetchOrder]);

  return (
    <OrderDetailContext.Provider value={OrderDetailContextContent}>
      <OrderDetailBreadCrumbs orderNumber={order.orderNumber} />
      {authResult && (
      <Alert
        className="pd-mb-3"
        variant={messages[authResult].type}
      >
        <FormattedMessage id={messages[authResult].id} />
      </Alert>
      )}
      <Grid className="pd-my-3 pd-grid-cols-3 pd-gap-4">
        <Grid.Item className="pd-col-span-2">
          <Heading as="h2">
            <FormattedMessage id="account.order.title" defaultMessage="Order title" />
            {' '}
            <span data-cy="orderNumberInTitle">{order.orderNumber}</span>
            {' | '}
            <time dateTime={order.createdAt}>
              <FormattedDate
                value={order.createdAt}
                year="numeric"
                month="long"
                day="2-digit"
              />
            </time>
          </Heading>
        </Grid.Item>
        <Grid.Item className="pd-col-span-1 pd-text-right">
          <PayOrderButton order={order} eventName="start_payment_top" />
        </Grid.Item>
      </Grid>
      <LineItemList lineItems={order.lineItems} />
      <OrderSummary order={order} />

      <Link
        as={GatsbyLink}
        to="../"
        variant="secondary"
        className="pd-mt-4 pd-text-xs"
        onClick={() => dispatchAccountEvent('order_detail_interaction', 'back_to_order_overview')}
        iconBefore="chevron-left"
      >
        <FormattedMessage id="account.order.backToOrders" defaultMessage="Back to orders" />
      </Link>
    </OrderDetailContext.Provider>
  );
};

export default OrderDetailWrapper;
